import React from 'react';
import DashboardLayout from './DashboardLayout';
import './DashboardAIInsights.css';

const DashboardAIInsights: React.FC = () => {
  const upcomingFeatures = [
    {
      icon: 'ri-brain-line',
      title: 'Threat Intelligence',
      description: 'AI-powered threat detection and analysis',
      status: 'In Development',
      eta: 'Q2 2024'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Predictive Analytics',
      description: 'Forecast security trends and vulnerabilities',
      status: 'Coming Soon',
      eta: 'Q3 2024'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Automated Response',
      description: 'AI-driven incident response and remediation',
      status: 'Planning',
      eta: 'Q4 2024'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Behavioral Analysis',
      description: 'Detect anomalous user and system behavior',
      status: 'Research',
      eta: '2025'
    },
    {
      icon: 'ri-robot-line',
      title: 'Security Assistant',
      description: 'Interactive AI assistant for security queries',
      status: 'Concept',
      eta: '2025'
    },
    {
      icon: 'ri-radar-line',
      title: 'Risk Scoring',
      description: 'AI-calculated security risk assessments',
      status: 'In Development',
      eta: 'Q2 2024'
    }
  ];

  const mockInsights = [
    {
      type: 'warning',
      title: 'Unusual Login Patterns Detected',
      description: 'AI has identified 15% increase in failed login attempts from unknown locations.',
      confidence: 85,
      time: '2 hours ago'
    },
    {
      type: 'info',
      title: 'Vulnerability Trend Analysis',
      description: 'Predicted 23% increase in web application vulnerabilities next month based on current trends.',
      confidence: 72,
      time: '4 hours ago'
    },
    {
      type: 'success',
      title: 'Security Posture Improvement',
      description: 'Overall security score improved by 12% following recent policy updates.',
      confidence: 91,
      time: '1 day ago'
    }
  ];

  return (
    <DashboardLayout title="AI Insights">
      <div className="dashboard-ai-insights">
        <div className="ai-insights-header">
          <div className="header-content">
            <div className="header-icon">
              <i className="ri-brain-line"></i>
            </div>
            <div className="header-text">
              <h1>AI Insights</h1>
              <p>Powered by Advanced Machine Learning</p>
            </div>
          </div>
          <div className="coming-soon-badge">
            <i className="ri-time-line"></i>
            Coming Soon
          </div>
        </div>

        <div className="ai-insights-content">
          <div className="preview-section">
            <div className="preview-card">
              <div className="preview-header">
                <h2>Get Early Access</h2>
                <p>Be among the first to experience our AI-powered security insights</p>
              </div>
              <div className="preview-features">
                <div className="feature-item">
                  <i className="ri-check-line"></i>
                  <span>Real-time threat analysis</span>
                </div>
                <div className="feature-item">
                  <i className="ri-check-line"></i>
                  <span>Predictive security alerts</span>
                </div>
                <div className="feature-item">
                  <i className="ri-check-line"></i>
                  <span>Automated risk assessment</span>
                </div>
                <div className="feature-item">
                  <i className="ri-check-line"></i>
                  <span>Intelligent recommendations</span>
                </div>
              </div>
              <div className="preview-actions">
                <button className="btn-primary">
                  <i className="ri-notification-line"></i>
                  Notify Me When Available
                </button>
                <button className="btn-secondary">
                  <i className="ri-external-link-line"></i>
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <div className="mock-insights-section">
            <h2>Preview: AI-Generated Insights</h2>
            <p className="section-description">Here's what you can expect from our AI insights platform</p>
            <div className="insights-grid">
              {mockInsights.map((insight, index) => (
                <div key={index} className={`insight-card ${insight.type}`}>
                  <div className="insight-header">
                    <div className="insight-type">
                      <i className={`ri-${insight.type === 'warning' ? 'alert-line' : insight.type === 'success' ? 'check-line' : 'information-line'}`}></i>
                      <span>{insight.type.toUpperCase()}</span>
                    </div>
                    <div className="confidence-score">
                      <span>{insight.confidence}% confidence</span>
                    </div>
                  </div>
                  <h3>{insight.title}</h3>
                  <p>{insight.description}</p>
                  <div className="insight-footer">
                    <span className="insight-time">{insight.time}</span>
                    <button className="insight-action">View Details</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="roadmap-section">
            <h2>Development Roadmap</h2>
            <p className="section-description">Track the progress of upcoming AI features</p>
            <div className="roadmap-grid">
              {upcomingFeatures.map((feature, index) => (
                <div key={index} className="roadmap-card">
                  <div className="roadmap-icon">
                    <i className={feature.icon}></i>
                  </div>
                  <div className="roadmap-content">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <div className="roadmap-meta">
                      <span className={`status ${feature.status.toLowerCase().replace(' ', '-')}`}>
                        {feature.status}
                      </span>
                      <span className="eta">ETA: {feature.eta}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="beta-signup-section">
            <div className="beta-card">
              <div className="beta-content">
                <h2>Join the Beta Program</h2>
                <p>Get exclusive access to our AI insights features before they're released to the public</p>
                <div className="beta-benefits">
                  <div className="benefit-item">
                    <i className="ri-star-line"></i>
                    <span>Early access to new features</span>
                  </div>
                  <div className="benefit-item">
                    <i className="ri-feedback-line"></i>
                    <span>Direct feedback channel to our team</span>
                  </div>
                  <div className="benefit-item">
                    <i className="ri-gift-line"></i>
                    <span>Special beta user benefits</span>
                  </div>
                </div>
              </div>
              <div className="beta-form">
                <input 
                  type="email" 
                  placeholder="Enter your email address"
                  className="beta-email-input"
                />
                <button className="btn-primary">
                  Join Beta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardAIInsights;
